/*
 * mischu-product-spec
 * Variant picker for the mischu — Product spec section, where each size/grind
 * is a SEPARATE product. Size pills + grind select choose a (size, grind) pair;
 * we look up the matching product entry and swap the hidden cart id, the button
 * price/label, the Details link and the main image. When a combination is out
 * of stock or doesn't exist, the add-to-cart button is disabled and its label
 * becomes the sold-out text. Add-to-cart is handled by Dawn's <product-form>.
 * Prices arrive pre-formatted from Liquid, so no money formatting here.
 */
if (!customElements.get('mischu-spec')) {
  customElements.define(
    'mischu-spec',
    class MischuSpec extends HTMLElement {
      connectedCallback() {
        const dataEl = this.querySelector('.mischu-spec__data');
        if (!dataEl) return;
        try {
          this.entries = JSON.parse(dataEl.textContent).entries || [];
        } catch (e) {
          return;
        }

        this.idInput = this.querySelector('[name="id"]');
        this.btnTextEl = this.querySelector('[data-btn-text]');
        this.btnPriceEl = this.querySelector('[data-btn-price]');
        this.submit = this.querySelector('[type="submit"]');
        this.detailsLink = this.querySelector('[data-details-link]');
        this.mainImg = this.querySelector('[data-main-img]');
        this.rowPriceEl = this.querySelector('[data-row-price]');
        this.stockLine = this.querySelector('[data-stock-line]');
        this.sizeInputs = Array.from(this.querySelectorAll('[data-role="size"]'));
        this.grindSelect = this.querySelector('[data-role="grind"]');

        this.addLabel = this.dataset.addLabel || 'Add to cart';
        this.soldOutText = this.dataset.soldOutText || 'Out of stock';
        // A price row outside the button means the button shouldn't repeat it.
        this.priceInButton = !this.rowPriceEl;

        this.sizeInputs.forEach((el) => el.addEventListener('change', () => this.onChange()));
        this.grindSelect?.addEventListener('change', () => this.onChange());

        this.onChange();
      }

      selectedSize() {
        const checked = this.sizeInputs.find((el) => el.checked);
        return checked ? checked.value : (this.sizeInputs[0] && this.sizeInputs[0].value) || null;
      }

      selectedGrind() {
        return this.grindSelect ? this.grindSelect.value : null;
      }

      matchEntry() {
        const size = this.selectedSize();
        const grind = this.selectedGrind();
        return this.entries.find((e) => e.size === size && (grind === null || e.grind === grind));
      }

      onChange() {
        // A single-variant product (the Aeropress) renders no selector at all,
        // so there is nothing to match on — its one entry is the answer.
        const entry =
          !this.sizeInputs.length && !this.grindSelect
            ? this.entries[0]
            : this.matchEntry();

        // No product for this combination — nothing to buy.
        if (!entry) {
          this.disable();
          return;
        }

        if (this.idInput) this.idInput.value = entry.id;
        if (this.detailsLink && entry.url) this.detailsLink.href = entry.url;
        if (this.mainImg && entry.image) {
          this.mainImg.src = entry.image;
          this.mainImg.removeAttribute('srcset');
        }

        if (entry.available) {
          this.enable(entry.price);
        } else {
          this.disable();
        }
      }

      enable(price) {
        if (this.submit) {
          this.submit.removeAttribute('disabled');
          this.submit.removeAttribute('aria-disabled');
        }
        if (this.btnTextEl) this.btnTextEl.textContent = this.priceInButton ? this.addLabel + ' — ' : this.addLabel;
        if (this.btnPriceEl && this.priceInButton) this.btnPriceEl.textContent = price;
        if (this.rowPriceEl && price) this.rowPriceEl.textContent = price;
        this.setStock(true);
      }

      disable() {
        if (this.submit) {
          this.submit.setAttribute('disabled', 'disabled');
          this.submit.setAttribute('aria-disabled', 'true');
        }
        if (this.btnTextEl) this.btnTextEl.textContent = this.soldOutText;
        if (this.btnPriceEl) this.btnPriceEl.textContent = '';
        this.setStock(false);
      }

      setStock(available) {
        if (!this.stockLine) return;
        const text = available
          ? this.stockLine.dataset.inStockText
          : this.stockLine.dataset.outOfStockText;
        this.stockLine.textContent = text || '';
        this.stockLine.hidden = !text;
        this.stockLine.classList.toggle('mischu-fp__stock--out', !available);
      }
    }
  );
}
